import { PageCenter } from "@/lib/react/page-center/page-center";
import Button, { ButtonProps } from "@mui/material/Button";
import type { NextApiRequest } from "next";
import { SessionProvider, signIn, signOut, useSession } from "next-auth/react";
import React from "react";
import { TApiAuthInfo } from "./api";
import { testMode } from "./queries";
import { ServerSession } from "./ssr";
import Typography from "@mui/material/Typography";
import Box from "@mui/system/Box";

const assumeAdminInDevelopmentMode = true;

export type AuthInfo = {
  isSignedIn: boolean;
  isAdmin: boolean;
  user: {
    email: string;
  } | null;
};

// Interface for NextApiRequest extended with auth-attributes
export interface NextApiRequestWithAuth extends NextApiRequest {
  auth: AuthInfo;
}

export const SignedIn = ({ children }: { children: React.ReactNode }) => {
  // if (!testMode()) {
  const auth = useSession();
  if (auth.status === "loading") return null;
  const { data: session } = auth;
  return session ? <>{children}</> : null;
  // } else {
  //   return isSignedIn() ? <>{children}</> : null;
  // }
};

export const SignedOut = ({ children }: { children: React.ReactNode }) => {
  // if (!testMode()) {
  const auth = useSession();
  if (auth.status === "loading") return null;
  const { data: session } = auth;
  return session ? null : <>{children}</>;
  // } else {
  //   return isSignedIn() ? null : <>{children}</>;
  // }
};

export const SignInButton = (
  props: {
    title?: string;
    options?: {
      callbackUrl?: string;
    };
  } & Omit<ButtonProps, "children" | "onClick">,
) => {
  const { title, ...buttonProps } = props;
  // if (!testMode()) {
  return (
    <Button
      color="inherit"
      {...buttonProps}
      onClick={() =>
        props.options?.callbackUrl
          ? signIn(undefined, { callbackUrl: props.options.callbackUrl })
          : signIn(undefined)
      }
    >
      {title ?? "Log in"}
    </Button>
  );
  // } else {
  //   return null;
  // }
};

export const SignUpButton = (
  props: {
    title?: string;
  } & Omit<ButtonProps, "children" | "onClick">,
) => {
  const { title, ...buttonProps } = props;
  // if (!testMode()) {
  const signUp = () => alert("Sign-up is not implemented yet.");
  return (
    <Button color="inherit" {...buttonProps} onClick={() => signUp()}>
      {title ?? "Sign up"}
    </Button>
  );
  // } else {
  //   return null;
  // }
};

export const SignOutButton = (
  props: {
    title?: string;
  } & Omit<ButtonProps, "children" | "onClick">,
) => {
  const { title, ...buttonProps } = props;
  // if (!testMode()) {
  return (
    <Button
      color="inherit"
      {...buttonProps}
      onClick={() => signOut({ redirect: true, callbackUrl: "/" })}
    >
      Log out
    </Button>
  );
  // } else {
  //   return null;
  // }
};

export const UserButton = (props: { afterSignOutUrl: string }) => {
  if (!testMode()) {
    return <SignOutButton />;
  } else {
    return null;
  }
};

export const useUser = () => {
  // if (!testMode()) {
  const { status, data: session } = useSession();
  const user =
    session && session.user
      ? { id: session.user.email, email: session.user.email }
      : null;
  return {
    isLoaded: status !== "loading",
    isSignedIn: !!session,
    user,
    isAdmin: session && (session as any as TApiAuthInfo).isAdmin,
  };
  // } else {
  //   const user = getUser();
  //   return {
  //     isLoaded: true,
  //     isSignedIn: isSignedIn(),
  //     user: user,
  //     isAdmin: user.email === "levente@bagi.name",
  //   };
  // }
};

export const AuthProvider = (props: any) => {
  if (!testMode()) {
    return (
      <SessionProvider session={props.session}>
        {props.children}
      </SessionProvider>
    );
  } else {
    return <>{props.children}</>;
  }
};

export const RequireSignIn = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <SignedIn>{children}</SignedIn>

      <SignedOut>
        <PageCenter>Please sign in in to view this page.</PageCenter>
      </SignedOut>
    </>
  );
};

export const hasAdminAccessLogic = (
  isAdmin: boolean | undefined | null,
): boolean => {
  if (
    assumeAdminInDevelopmentMode &&
    process.env["NODE_ENV"] === "development"
  ) {
    // return true;
  }
  return isAdmin ?? false;
};

export const hasAdminAccess = (session: ServerSession | undefined | null) => {
  return hasAdminAccessLogic(session?.isAdmin);
};

/**
 * Client-side counterpart of `hasAdminAccess`: reads `isAdmin` off the session
 * (set by the NextAuth `session` callback) and applies the same dev override.
 * `isLoaded` is false while the session is still being fetched, so callers can
 * avoid flashing a "not an admin" state at actual admins.
 */
export const useAdminAccess = () => {
  const { isLoaded, isAdmin } = useUser();
  return {
    isLoaded,
    hasAdminAccess: hasAdminAccessLogic(isAdmin),
  };
};

export const AdminAccessRequiredNotice = () => (
  <PageCenter>
    Please sign in as an admin to view this page.
    {process.env["NODE_ENV"] === "development" && (
      <Box mt={2}>
        <Typography variant="caption">
          In development mode: edit <code>assumeAdminInDevelopmentMode</code> in{" "}
          <code>src/modules/authentication/index.tsx</code>
        </Typography>
      </Box>
    )}
  </PageCenter>
);

export const renderForAdminOnly = (
  session: ServerSession | undefined,
  callback: () => React.ReactElement,
): React.ReactElement => {
  if (hasAdminAccess(session)) {
    return callback();
  } else {
    return <AdminAccessRequiredNotice />;
  }
};

export const requireSignIn = (
  session: ServerSession | undefined,
  options: { callbackUrl?: string } | undefined,
  callback: (session: ServerSession) => React.ReactElement,
): React.ReactElement => {
  if (session?.user) {
    return callback(session);
  } else {
    return (
      <PageCenter>
        <Box sx={{ textAlign: "center" }}>
          <Typography mb={2}>Please sign in to view this page.</Typography>
          <SignInButton variant="contained" color="primary" options={options} />
        </Box>
      </PageCenter>
    );
  }
};
